import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { fetchLeadActivities, fetchLeadActivitySummary } from "../../api/leadActivityApi";
import { fetchStageHistory } from "../../api/stageHistoryApi";
import ActivitySummary from "./ActivitySummary";
import ActivityCard from "./ActivityCard";
import StageHistoryCard from "./StageHistoryCard";
import LogActivityModal from "./LogActivityModal";
import {
  FaPlus,
  FaRedo,
  FaHistory,
  FaComments,
  FaExchangeAlt,
  FaSearch,
  FaExclamationTriangle,
  FaLock,
} from "react-icons/fa";

function LeadTimeline({ leadId, currentStatus = "", currentUserId = "" }) {
  const [activeTab, setActiveTab] = useState("activities"); // "activities" | "stageHistory"
  const [activities, setActivities] = useState([]);
  const [stageHistory, setStageHistory] = useState([]);
  const [summaryData, setSummaryData] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isLeadClosed =
    currentStatus === "DISQUALIFIED" || currentStatus === "CONVERTTOCUSTOMER";

  // Parallel data fetching via Promise.all
  const fetchTimelineData = useCallback(async () => {
    if (!leadId) return;

    setLoading(true);
    setError(null);

    try {
      const [activitiesRes, stageHistoryRes, summaryRes] = await Promise.allSettled([
        fetchLeadActivities(leadId),
        fetchStageHistory(leadId),
        fetchLeadActivitySummary(leadId),
      ]);

      if (activitiesRes.status === "fulfilled") {
        setActivities(Array.isArray(activitiesRes.value) ? activitiesRes.value : []);
      } else {
        setActivities([]);
      }

      if (stageHistoryRes.status === "fulfilled") {
        setStageHistory(Array.isArray(stageHistoryRes.value) ? stageHistoryRes.value : []);
      } else {
        setStageHistory([]);
      }

      if (summaryRes.status === "fulfilled" && summaryRes.value) {
        setSummaryData(summaryRes.value || {});
      } else {
        setSummaryData({});
      }

      if (
        activitiesRes.status === "rejected" &&
        stageHistoryRes.status === "rejected"
      ) {
        throw new Error(activitiesRes.reason?.message || "Failed to load lead timeline data.");
      }
    } catch (err) {
      console.error("Error loading timeline data:", err);
      setError(err.message || "Could not load timeline data. Please check connection.");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchTimelineData();
  }, [fetchTimelineData]);

  // Filtered Activities
  const filteredActivities = useMemo(() => {
    return activities
      .filter((act) => {
        if (filterType !== "ALL") {
          const actType = (act.type || "").toUpperCase();
          if (actType !== filterType) return false;
        }

        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const matchesSubject = act.subject && act.subject.toLowerCase().includes(q);
          const matchesNotes = act.notes && act.notes.toLowerCase().includes(q);
          const matchesUser = (act.user || act.createdBy) && (act.user || act.createdBy).toLowerCase().includes(q);
          const matchesOutcome = act.outcome && act.outcome.toLowerCase().includes(q);
          return matchesSubject || matchesNotes || matchesUser || matchesOutcome;
        }

        return true;
      })
      .sort((a, b) => new Date(b.activityDate || b.createdDate || b.date || 0) - new Date(a.activityDate || a.createdDate || a.date || 0));
  }, [activities, filterType, searchQuery]);

  // Filtered Stage History
  const sortedStageHistory = useMemo(() => {
    return [...stageHistory].sort((a, b) => new Date(a.changedAt || a.createdDate || a.date || 0) - new Date(b.changedAt || b.createdDate || b.date || 0));
  }, [stageHistory]);

  return (
    <div className="card border-0 shadow-sm rounded-3 bg-white mb-4 overflow-hidden">
      {/* Header Bar */}
      <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
        {/* Navigation Tabs */}
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm px-3.5 py-2 rounded-pill fw-bold d-inline-flex align-items-center gap-2 transition-all"
            style={{
              backgroundColor: activeTab === "activities" ? "#8b5cf6" : "#f1f5f9",
              color: activeTab === "activities" ? "#ffffff" : "#475569",
              border: "none",
              fontSize: "0.85rem",
            }}
            onClick={() => setActiveTab("activities")}
          >
            <FaComments size={14} /> Activities ({activities.length})
          </button>

          <button
            type="button"
            className="btn btn-sm px-3.5 py-2 rounded-pill fw-bold d-inline-flex align-items-center gap-2 transition-all"
            style={{
              backgroundColor: activeTab === "stageHistory" ? "#8b5cf6" : "#f1f5f9",
              color: activeTab === "stageHistory" ? "#ffffff" : "#475569",
              border: "none",
              fontSize: "0.85rem",
            }}
            onClick={() => setActiveTab("stageHistory")}
          >
            <FaExchangeAlt size={14} /> Stage History ({stageHistory.length})
          </button>
        </div>

        {/* Header Right Actions */}
        <div className="d-flex align-items-center gap-2">
          {isLeadClosed ? (
            <div className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1.5" style={{ fontSize: "0.8rem" }}>
              <FaLock size={12} /> Lead is closed, no new activities allowed
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-sm px-3.5 py-2 rounded-pill fw-bold d-inline-flex align-items-center gap-1.5 shadow-sm"
              style={{ backgroundColor: "#8b5cf6", color: "#ffffff", border: "none", fontSize: "0.83rem" }}
              onClick={() => setIsModalOpen(true)}
            >
              <FaPlus size={12} /> Log Activity
            </button>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm p-2 rounded-circle"
            onClick={fetchTimelineData}
            title="Refresh Timeline"
          >
            <FaRedo size={12} />
          </button>
        </div>
      </div>

      {/* Main Body */}
      <div className="card-body p-4">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border mb-2" role="status" style={{ width: "2rem", height: "2rem", color: "#8b5cf6" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted small mb-0">Fetching timeline data...</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="alert alert-danger p-3 rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
            <div className="d-flex align-items-center gap-2">
              <FaExclamationTriangle className="fs-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
            <button type="button" className="btn btn-sm btn-outline-danger px-3 rounded-2 fw-bold" onClick={fetchTimelineData}>
              <FaRedo className="me-1" /> Retry
            </button>
          </div>
        )}

        {/* Tab 1: Activities */}
        {!loading && !error && activeTab === "activities" && (
          <div>
            {/* Interactive Activity Summary Filter Bar + Search */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-3 border-bottom">
              <ActivitySummary
                summaryData={summaryData}
                activities={activities}
                filterType={filterType}
                onSelectFilter={setFilterType}
              />

              {/* Search Box */}
              <div className="input-group input-group-sm ms-auto" style={{ width: "220px" }}>
                <span className="input-group-text bg-white border-end-0 text-muted" style={{ borderTopLeftRadius: "6px", borderBottomLeftRadius: "6px" }}>
                  <FaSearch size={11} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Search activities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: "0.82rem", borderTopRightRadius: "6px", borderBottomRightRadius: "6px" }}
                />
              </div>
            </div>

            {/* Activity Timeline List */}
            {filteredActivities.length === 0 ? (
              <div className="text-center py-5 border rounded-3 bg-light">
                <FaComments className="text-muted mb-2" size={36} />
                <p className="text-secondary small fw-medium mb-1">
                  No activities logged yet. Log a call, email, or meeting to start the timeline.
                </p>
                {!isLeadClosed && (
                  <button
                    type="button"
                    className="btn btn-sm text-white mt-2 px-3.5 py-1.5 rounded-pill fw-bold shadow-xs"
                    style={{ backgroundColor: "#8b5cf6", border: "none" }}
                    onClick={() => setIsModalOpen(true)}
                  >
                    <FaPlus className="me-1" /> Log First Activity
                  </button>
                )}
              </div>
            ) : (
              <div className="position-relative ps-3 ps-md-4" style={{ borderLeft: "2px solid #e2e8f0", marginLeft: "12px" }}>
                {filteredActivities.map((activity) => (
                  <ActivityCard key={activity.id || activity._id || Math.random()} activity={activity} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Stage History */}
        {!loading && !error && activeTab === "stageHistory" && (
          <div>
            <div className="mb-3 text-muted small">
              Shows stage transitions from initial creation to conversion or drop-off (oldest to newest).
            </div>

            {sortedStageHistory.length === 0 ? (
              <div className="text-center py-5 border rounded-3 bg-light">
                <FaHistory className="text-muted mb-2" size={36} />
                <p className="text-secondary small fw-medium mb-0">
                  No stage changes recorded yet. Stage history updates automatically when an activity changes the lead's status.
                </p>
              </div>
            ) : (
              <div className="position-relative ps-3 ps-md-4" style={{ borderLeft: "2px solid #e2e8f0", marginLeft: "12px" }}>
                {sortedStageHistory.map((history) => (
                  <StageHistoryCard key={history.id || history._id || Math.random()} history={history} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Log Activity Modal */}
      <LogActivityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchTimelineData}
        leadId={leadId}
        currentStatus={currentStatus}
        currentUserId={currentUserId}
      />
    </div>
  );
}

LeadTimeline.propTypes = {
  leadId: PropTypes.string.isRequired,
  currentStatus: PropTypes.string,
  currentUserId: PropTypes.string,
};

export default LeadTimeline;
